import type { AddressData } from "@louise/moon-ui";
import { normalizeCountryCode } from "@src/packages/location/countryPreference";

/**
 * The rules behind the Address box and the Country field on Personal info.
 * The country is hers: it changes only when a picked place positively names
 * one or when she chooses one in the Country field. Typing and clearing the
 * address never touch it.
 */

type SavedAddress = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
};

/** What the address field reported, folded into the draft. */
export function applyAddressChange(
  current: AddressData | null,
  next: AddressData,
): AddressData {
  return { ...next, country: next.country ?? current?.country ?? null };
}

/** The clear control: the address goes, the country stays. */
export function clearAddress(current: AddressData | null): AddressData {
  return {
    address1: null,
    address2: null,
    city: null,
    state: null,
    postalCode: null,
    country: current?.country ?? null,
  };
}

/** The Country field: her choice, over whatever address text there is. */
export function chooseAddressCountry(
  current: AddressData | null,
  saved: SavedAddress | undefined,
  code: string,
): AddressData {
  const base: AddressData = current ?? {
    address1: saved?.address1 ?? null,
    address2: saved?.address2 ?? null,
    city: saved?.city ?? null,
    state: null,
    postalCode: saved?.postalCode ?? null,
    country: null,
  };
  return { ...base, country: code };
}

/** The country the Country field shows: the draft's when it names one, else the saved one. */
export function resolveAddressCountry(
  draft: AddressData | null,
  saved: SavedAddress | undefined,
): string | null {
  return (
    (draft ? normalizeCountryCode(draft.country) : null) ??
    normalizeCountryCode(saved?.country)
  );
}

/** What a save writes for the address record. */
export function buildAddressSave(
  draft: AddressData,
  saved: SavedAddress | undefined,
): Required<SavedAddress> {
  return {
    address1: draft.address1?.trim() || null,
    address2: draft.address2,
    city: draft.city,
    postalCode: draft.postalCode,
    country: draft.country ?? saved?.country ?? null,
  };
}
